import { useEffect, useMemo, useState } from "react";
import { deserializeLocationSearchIndex } from "../Components/Booking/locationSearch";
import client from "../api/client";

const cache = new Map();

const normalizeMode = (mode) => {
  const raw = String(mode || "").trim().toLowerCase();
  if (!raw || raw === "all") return "all";
  return raw;
};

const buildCacheKey = (options = {}) => {
  const mode = normalizeMode(options.mode);
  return mode;
};

const buildQuery = (options = {}) => {
  const params = new URLSearchParams();
  const mode = normalizeMode(options.mode);
  if (mode !== "all") params.set("mode", mode);
  const query = params.toString();
  return query ? `?${query}` : "";
};

const fetchLocationIndex = async (options = {}) => {
  const payload = await client.get(`/places/suggestions${buildQuery(options)}`);
  return deserializeLocationSearchIndex(payload);
};

export const preloadLocationSearchIndex = (options = {}) => {
  const key = buildCacheKey(options);
  const cached = cache.get(key) || {};
  if (cached.inflight) {
    return cached.inflight;
  }
  const inflight = fetchLocationIndex(options)
    .then((data) => {
      cache.set(key, { ...cached, index: data, error: null, inflight: null });
      return data;
    })
    .catch((error) => {
      cache.set(key, { ...cached, index: null, error, inflight: null });
      console.error("Failed to fetch live suggestions", error);
      throw error;
    });
  cache.set(key, { ...cached, inflight });
  return inflight;
};

export function useLocationSearchIndex(options = {}) {
  const enabled = options.enabled !== false;
  const key = buildCacheKey(options);
  const requestOptions = useMemo(() => ({ mode: key }), [key]);
  const cached = cache.get(key) || {};
  const [state, setState] = useState(() => ({
    index: cached.index || null,
    error: cached.error || null,
    status: cached.index ? "success" : cached.error ? "error" : enabled ? "loading" : "idle",
  }));

  useEffect(() => {
    const currentCached = cache.get(key) || {};
    if (!enabled) {
      setState({
        index: currentCached.index || null,
        error: currentCached.error || null,
        status: currentCached.index ? "success" : currentCached.error ? "error" : "idle",
      });
      return undefined;
    }
    let cancelled = false;
    const request = preloadLocationSearchIndex(requestOptions);

    setState({
      index: currentCached.index || null,
      error: currentCached.error || null,
      status: currentCached.index ? "success" : "loading",
    });

    request
      .then((data) => {
        if (cancelled) return;
        setState({ index: data, error: null, status: "success" });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ index: null, error, status: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, key, requestOptions]);

  const refresh = () =>
    preloadLocationSearchIndex(requestOptions)
      .then((data) => {
        setState({ index: data, error: null, status: "success" });
        return data;
      })
      .catch((error) => {
        setState({ index: null, error, status: "error" });
        return null;
      });

  return {
    index: state.index,
    error: state.error,
    status: state.status,
    isLoading: state.status === "loading",
    isReady: state.status === "success",
    refresh,
  };
}
